/* ==========================================================================
   Gift message — a note the shopper writes in the CART, stored as a Shopify
   cart attribute so it appears on the order in admin for the packing team.

   Shopify's checkout markup cannot be changed without Plus + Checkout
   Extensibility, which is why this lives in the cart rather than at checkout.
   ========================================================================== */

(function () {
  'use strict';

  var CFG = window.giftMessage || {};
  var STR = CFG.strings || {};

  var ATTR_MESSAGE = 'Gift message';
  var ATTR_BAG = 'Gift bag';

  /* ---------------------------------------------------------------- utils */

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function getCart() {
    // no Accept header: Shopify content-negotiates and would ignore our view
    return fetch('/cart.js').then(function (r) { return r.json(); });
  }

  function updateCart(body) {
    return fetch((window.routes && window.routes.cart_update_url) || '/cart/update', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.text().then(function (text) {
        var parsed = null;
        try { parsed = JSON.parse(text); } catch (e) { parsed = null; }
        if (!response.ok) {
          var reason = '';
          if (parsed) {
            reason = parsed.description || parsed.message || '';
            if (!reason && parsed.errors) {
              reason = typeof parsed.errors === 'string' ? parsed.errors : '';
            }
          }
          throw new Error(reason);
        }
        return parsed;
      });
    });
  }

  function refreshCartUI() {
    if (window.ajaxCart && typeof window.ajaxCart.load === 'function') {
      window.ajaxCart.load();
    } else {
      window.location.reload();
    }
  }


  /**
   * The cart drawer installs a jQuery focus trap (mono.js Drawer.trapFocus):
   *   $(document).on('focusin.drawer_focus', ... $container.focus() ...)
   * which yanks focus back to #CartDrawer whenever anything outside it gains
   * focus. A dialog rendered outside the drawer therefore cannot be typed in —
   * clicks land, focus is stolen, and the field looks disabled.
   * Release the trap while the dialog is open, restore it afterwards.
   */
  function releaseDrawerFocusTrap() {
    if (window.jQuery) {
      try { window.jQuery(document).off('focusin.drawer_focus'); } catch (e) { /* noop */ }
    }
  }

  function restoreDrawerFocusTrap() {
    if (!window.jQuery) return;
    if (!document.body.classList.contains('js-drawer-open')) return;
    var drawer = window.jQuery('#CartDrawer');
    if (!drawer.length) return;
    if (window.mono && window.mono.RightDrawer && typeof window.mono.RightDrawer.trapFocus === 'function') {
      try { window.mono.RightDrawer.trapFocus(drawer, 'drawer_focus'); } catch (e) { /* noop */ }
    }
  }

  /* ---------------------------------------------------------------- modal */

  var Modal = {
    root: null,
    text: null,
    bag: null,
    count: null,
    status: null,

    init: function () {
      this.root = document.getElementById('GiftMessageModal');
      if (!this.root) return;

      this.text = this.root.querySelector('.gift-modal__text');
      this.bag = this.root.querySelector('#GiftMessageBag');
      this.count = this.root.querySelector('[data-gift-count]');
      this.status = this.root.querySelector('.gift-modal__status');

      var self = this;

      this.root.addEventListener('click', function (event) {
        if (event.target.closest('[data-gift-close]')) {
          event.preventDefault();
          self.close();
        } else if (event.target.closest('[data-gift-save]')) {
          event.preventDefault();
          self.save();
        }
      });

      if (this.text) {
        this.text.addEventListener('input', function () { self.updateCount(); });
      }

      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && !self.root.hidden) self.close();
      });

      document.addEventListener('click', function (event) {
        if (!event.target.closest('[data-gift-open]')) return;
        event.preventDefault();
        self.open();
      });
    },

    updateCount: function () {
      if (!this.count || !this.text) return;
      var max = parseInt(this.count.getAttribute('data-gift-max'), 10) || 150;
      var remaining = Math.max(0, max - this.text.value.length);
      this.count.textContent = remaining + ' /' + max + ' characters';
    },

    open: function () {
      var self = this;
      this.setStatus('');

      // prefill from whatever is already on the cart
      getCart()
        .then(function (cart) {
          var attrs = cart.attributes || {};
          if (self.text) self.text.value = attrs[ATTR_MESSAGE] || '';
          if (self.bag) self.bag.checked = !!attrs[ATTR_BAG];
          self.updateCount();
        })
        .catch(function () { /* an empty form is still usable */ })
        .finally(function () {
          releaseDrawerFocusTrap();
          self.root.hidden = false;
          document.body.classList.add('gift-modal-open');
          if (self.text) self.text.focus();
        });
    },

    close: function () {
      this.root.hidden = true;
      document.body.classList.remove('gift-modal-open');
      restoreDrawerFocusTrap();
    },

    setStatus: function (text, isError) {
      if (!this.status) return;
      this.status.textContent = text || '';
      this.status.classList.toggle('gift-modal__status--error', !!isError);
    },

    save: function () {
      var self = this;
      var button = this.root.querySelector('[data-gift-save]');
      var message = this.text ? this.text.value.trim() : '';
      var wantsBag = this.bag ? this.bag.checked : false;

      button.disabled = true;
      this.setStatus(STR.saving || 'Saving…');

      var attributes = {};
      attributes[ATTR_MESSAGE] = message;
      attributes[ATTR_BAG] = wantsBag ? (STR.bagYes || 'Yes') : '';

      var payload = { attributes: attributes };

      // only touch quantities when a chargeable gift-bag product is configured
      if (CFG.bagVariantId) {
        payload.updates = {};
        payload.updates[CFG.bagVariantId] = wantsBag ? 1 : 0;
      }

      updateCart(payload)
        .then(function () {
          self.setStatus(STR.saved || 'Saved');
          window.setTimeout(function () {
            self.close();
            refreshCartUI();
          }, 600);
        })
        .catch(function (error) {
          self.setStatus(error.message || STR.error || 'Could not save your gift message', true);
        })
        .finally(function () {
          button.disabled = false;
        });
    }
  };

  /* ------------------------------------------------- trigger + cart summary */

  function cartFooter() {
    // .totals is a flex row (subtotal label + value); inserting into it squashes
    // everything into columns, so use its block-level parent on the cart page.
    var drawerFooter = document.querySelector('#CartContainer .ajaxcart__footer');
    var totals = document.querySelector('.totals');
    return drawerFooter || (totals && totals.parentElement) || document.querySelector('.cart__blocks');
  }

  function render(cart) {
    if (!CFG.enabled) return;
    var host = cartFooter();
    if (!host || !cart || !cart.items || !cart.items.length) return;
    if (host.querySelector('.gift-trigger')) return;

    var attrs = cart.attributes || {};
    var message = attrs[ATTR_MESSAGE] || '';

    var wrap = el('div', 'gift-trigger');

    if (message) {
      var saved = el('div', 'gift-trigger__saved');
      saved.appendChild(el('strong', 'gift-trigger__saved-label', STR.savedLabel || 'Gift message'));
      saved.appendChild(el('p', 'gift-trigger__saved-text', message));
      if (attrs[ATTR_BAG]) {
        saved.appendChild(el('span', 'gift-trigger__bag', STR.bagIncluded || 'Gift bag included'));
      }
      wrap.appendChild(saved);
    }

    var button = el('button', 'gift-trigger__button', message ? (STR.edit || 'Edit gift message') : (STR.add || '+ Add a gift message'));
    button.type = 'button';
    button.setAttribute('data-gift-open', 'true');
    wrap.appendChild(button);

    // sit above the price summary so it reads before the numbers
    var anchor =
      host.querySelector('.pack-totals') ||
      host.querySelector('.totals') ||
      host.firstChild;
    if (anchor) host.insertBefore(wrap, anchor);
    else host.appendChild(wrap);

    hideLegacyCartNote();
  }

  /**
   * The theme's own "Add cart note" duplicates this feature. Hide it only once
   * the gift message UI has actually rendered, so disabling the feature brings
   * the original note back untouched.
   */
  function hideLegacyCartNote() {
    var notes = document.querySelectorAll('.note_text, cart-note.cart__note');
    Array.prototype.forEach.call(notes, function (node) {
      node.hidden = true;
      node.style.display = 'none';
    });
  }

  function refresh() {
    if (!CFG.enabled) return;
    if (!cartFooter()) return;
    getCart().then(render).catch(function () { /* cosmetic */ });
  }

  /* ----------------------------------------------------------------- boot */

  function boot() {
    Modal.init();
    refresh();

    if (window.jQuery) {
      window.jQuery(document.body).on('afterCartLoad.ajaxCart', function (event, cart) {
        render(cart);
      });
    }

    if (window.MutationObserver) {
      var pending = null;
      new MutationObserver(function () {
        if (pending) return;
        pending = window.setTimeout(function () {
          pending = null;
          refresh();
        }, 250);
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
